import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ChevronDown, Menu, Search } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { APP_NAME, EMPTY_INVENTORY_FILTERS } from '@amzjp/shared';
import { encodeFilters, readFiltersFromUrl } from '@/features/inventory/url-state';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  KISA_AD,
  menuBaglantisiTiklandi,
  NAVIGATION,
  type NavItem,
  type NavSection,
} from './navigation';
import { TopbarAraclari } from './topbar';
import { MarkaYazisi, UlkeRozeti } from './marka';

/**
 * Üst menü (sahip, 28 Eyl 2026: "menü barlarını üste alıp daha sade yapabilirsin
 * düzeni" — SellerRunning örneği).
 *
 * Kenar çubuğu 11 bölüm ve 50'yi aşkın alt maddeyle ekranın solunu sürekli
 * kaplıyordu. Aynı menü ağacı (`navigation.ts`) burada açılır menüler olarak
 * çiziliyor: rotalar, etiketler ve yetkiler aynen duruyor, yalnızca yerleşim
 * değişiyor. "Sistem" kümesindeki iki bölüm (İşlem Geçmişi, Ayarlar) tek bir açılır
 * menüde sütun sütun; sağında "Amazon Araçları" (21 Eki 2026: bütün araçlar bir arada).
 *
 * Dar ekranda (<1280px) menü sığmıyor: yerine ☰ düğmesi çıkıyor ve eski kenar
 * menüsü çekmece olarak açılıyor.
 */

/** 1536px altında: 10 menü, ad ve araçlar 1280px'e ancak böyle sığıyor. */
const DAR_AD: Record<string, string> = { 'Amazon Araçları': 'Araçlar' };

interface UstOge {
  etiket: string;
  /** Doğrudan bağlantı (alt menüsü yoksa). */
  to?: string;
  /** Tek sütunlu açılır menü. */
  maddeler?: NavItem[];
  /** Çok sütunlu açılır menü (Sistem). */
  sutunlar?: Array<{ baslik: string; maddeler: NavItem[] }>;
}

function ustMenu(): UstOge[] {
  const oge = (b: NavSection): UstOge =>
    b.children
      ? { etiket: KISA_AD[b.label] ?? b.label, maddeler: b.children }
      : { etiket: KISA_AD[b.label] ?? b.label, to: b.to };
  const sistem = NAVIGATION.filter((b) => b.group === 'sistem');
  return [
    ...NAVIGATION.filter((b) => b.group !== 'sistem' && b.group !== 'araclar').map(oge),
    {
      etiket: 'Sistem',
      sutunlar: sistem.map((b) => ({
        baslik: b.label,
        maddeler: b.children ?? [{ label: b.label, to: b.to }],
      })),
    },
    // Sistem'in sağında: bütün araçlar tek menüde (21 Eki 2026)
    ...NAVIGATION.filter((b) => b.group === 'araclar').map(oge),
  ];
}

const UST_MENU = ustMenu();

function aktifMi(o: UstOge, yol: string): boolean {
  if (o.to !== undefined)
    return o.to === '/' ? yol === '/' : yol === o.to || yol.startsWith(`${o.to}/`);
  const hepsi = o.maddeler ?? o.sutunlar?.flatMap((s) => s.maddeler) ?? [];
  return hepsi.some((m) => yol === m.to || (m.to !== '/' && yol.startsWith(`${m.to}/`)));
}

export function TopNav({ onMenuAc }: { onMenuAc: () => void }) {
  const yol = useRouterState({ select: (s) => s.location.pathname });
  const [acik, setAcik] = useState<string | null>(null);
  const kapsayici = useRef<HTMLDivElement>(null);

  // Sayfa değişince açık menü kapansın
  useEffect(() => setAcik(null), [yol]);

  useEffect(() => {
    if (acik === null) return;
    const disari = (e: MouseEvent) => {
      if (kapsayici.current && !kapsayici.current.contains(e.target as Node)) setAcik(null);
    };
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcik(null);
    };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', tus);
    return () => {
      document.removeEventListener('mousedown', disari);
      document.removeEventListener('keydown', tus);
    };
  }, [acik]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg-elevated/85 backdrop-blur supports-[backdrop-filter]:bg-bg-elevated/70">
      {/*
        Çubuk içerik gibi 1440px'e sıkıştırılmıyor, ekranın tamamını kullanıyor
        (29 Eyl 2026): 9 menü + arama + düğmeler 1440px'e ancak sığıyordu; 1280'de
        ve 1536+'da (arama genişleyince) hesap düğmesi ekran dışına taşıyordu.
      */}
      <div className="flex h-[var(--nav-h)] w-full items-center gap-3 px-6 max-lg:px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuAc}
          aria-label="Menüyü aç"
          className="xl:hidden"
        >
          <Menu aria-hidden="true" />
        </Button>

        <Link
          to="/"
          className="flex shrink-0 items-center gap-2.5"
          aria-label={`${APP_NAME} ana sayfa`}
        >
          <img
            src="/logo.svg"
            alt=""
            className="size-8 rounded-[var(--radius-sm)]"
            aria-hidden="true"
          />
          <MarkaYazisi className="max-sm:hidden" />
          <UlkeRozeti />
        </Link>

        <nav
          ref={kapsayici}
          aria-label="Ana menü"
          className="ml-2 hidden items-center gap-0.5 xl:flex"
        >
          {UST_MENU.map((o) => {
            const aktif = aktifMi(o, yol);
            const temel = cn(
              'relative inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-[var(--radius-sm)] px-1.5 text-[0.8125rem] font-medium transition-colors 3xl:px-2.5',
              aktif ? 'text-accent' : 'text-fg-muted hover:bg-bg-sunken hover:text-fg',
              aktif &&
                'after:absolute after:inset-x-1.5 after:-bottom-[0.6875rem] after:h-[2px] after:rounded-full after:bg-accent 3xl:after:inset-x-2.5',
            );
            const darAd = DAR_AD[o.etiket];
            const yazi = darAd ? (
              <>
                <span className="2xl:hidden">{darAd}</span>
                <span className="max-2xl:hidden">{o.etiket}</span>
              </>
            ) : (
              o.etiket
            );
            if (o.to !== undefined) {
              return (
                <Link
                  key={o.etiket}
                  to={o.to}
                  className={temel}
                  onClick={(e) => menuBaglantisiTiklandi(e, o.to!)}
                >
                  {yazi}
                </Link>
              );
            }
            const buAcik = acik === o.etiket;
            return (
              <div key={o.etiket} className="relative">
                <button
                  type="button"
                  className={temel}
                  aria-expanded={buAcik}
                  aria-haspopup="menu"
                  aria-label={darAd ? o.etiket : undefined}
                  onClick={() => setAcik(buAcik ? null : o.etiket)}
                >
                  {yazi}
                  <ChevronDown
                    className={cn(
                      'size-3.5 transition-transform max-genis:hidden',
                      buAcik && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </button>
                {buAcik && <AcilirPanel oge={o} yol={yol} kapat={() => setAcik(null)} />}
              </div>
            );
          })}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          <GenelArama />
          <TopbarAraclari />
        </div>
      </div>
    </header>
  );
}

function AcilirPanel({ oge, yol, kapat }: { oge: UstOge; yol: string; kapat: () => void }) {
  const madde = (m: NavItem) => (
    <li key={m.to + m.label}>
      <Link
        to={m.to}
        role="menuitem"
        // Açık sayfanın maddesinde yol değişmiyor, menü kendiliğinden kapanmıyordu
        onClick={(e) => {
          kapat();
          menuBaglantisiTiklandi(e, m.to);
        }}
        className={cn(
          'block rounded-[var(--radius-sm)] px-3 py-2 text-[0.8125rem] transition-colors',
          yol === m.to ? 'bg-accent-soft font-medium text-accent' : 'text-fg hover:bg-bg-sunken',
        )}
      >
        {m.label}
      </Link>
    </li>
  );

  return (
    <div
      role="menu"
      className={cn(
        'menu-acilis absolute left-0 top-[calc(100%+0.625rem)] z-50 rounded-[var(--radius-lg)] border border-border bg-bg-elevated p-2 shadow-[var(--shadow-pop)]',
        /*
          Sütun sayısı kadar genişlik (23 Eki 2026, sahip: "sistem kısmını açınca sağda boşluk
          kalıyor"). Sabit üç sütunluk 40rem, Amazon Araçları ayrı menüye çıkınca (21 Eki) iki
          gruba kalan Sistem'de üçüncü sütunu boş bırakıyordu.
        */
        oge.sutunlar
          ? 'grid w-max grid-flow-col auto-cols-[13.5rem] gap-2 max-2xl:right-0 max-2xl:left-auto'
          : 'w-60',
      )}
    >
      {oge.maddeler && <ul className="space-y-0.5">{oge.maddeler.map(madde)}</ul>}
      {oge.sutunlar?.map((s) => (
        <div key={s.baslik}>
          <p className="px-3 pb-1.5 pt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
            {s.baslik}
          </p>
          <ul className="space-y-0.5">{s.maddeler.map(madde)}</ul>
        </div>
      ))}
    </div>
  );
}

/**
 * ASIN, SKU ya da sipariş numarası ile arama (SellerRunning'deki kutu).
 * Sipariş numarası biçimindeyse doğrudan sipariş detayına, değilse envantere
 * arama sözcüğüyle gidiyor.
 */
function GenelArama() {
  const navigate = useNavigate();
  const [metin, setMetin] = useState('');

  const gonder = (e: FormEvent) => {
    e.preventDefault();
    const q = metin.trim();
    if (q === '') return;
    setMetin('');
    // Dar çubukta kutu odak gidince büyütece dönüyor; Enter sonrası menünün üstünde kalmasın
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (/^\d{3}-\d{7}-\d{7}$/.test(q)) {
      void navigate({ to: `/siparisler/${q}` as never });
      return;
    }
    // Envanter süzgeçlerini adres çubuğundan okuyor (`?f=`); serbest metin ASIN/SKU/başlık/marka arar
    // Envanter açıksa sayfa boyutu ve sıralama korunur; yalnız süzgeç arama olur
    const acik = window.location.pathname === '/envanter' ? readFiltersFromUrl() : null;
    const f = encodeFilters({
      ...EMPTY_INVENTORY_FILTERS,
      ...(acik?.limit ? { limit: acik.limit } : {}),
      ...(acik?.sort ? { sort: acik.sort } : {}),
      general: { search: q },
      withCount: true,
    } as never);
    if (window.location.pathname === '/envanter') {
      // Sayfa zaten açık: bileşen yeniden kurulmuyor, geri tuşu olayıyla süzgeci okutuyoruz
      window.history.pushState({}, '', `/envanter?f=${f}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    void navigate({ to: '/envanter' as never, search: { f } as never });
  };

  return (
    /*
      1280–1679px arasında kutu çubuğa sığmıyor (hesap düğmesi ekran dışına taşıyordu): yalnız büyüteç
      görünüyor, tıklayınca menünün üstüne doğru açılıyor. Odak gidince geri kapanıyor, yazı duruyor;
      açık kalsa menünün sağını örtüp tıklamayı alıyordu.
    */
    <form onSubmit={gonder} className="relative mr-1 max-md:hidden xl:max-3xl:size-9" role="search">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 z-20 size-4 -translate-y-1/2 text-fg-subtle xl:max-3xl:left-auto xl:max-3xl:right-2.5"
        aria-hidden="true"
      />
      <input
        type="search"
        value={metin}
        onChange={(e) => setMetin(e.target.value)}
        placeholder="ASIN, SKU, sipariş no"
        aria-label="ASIN, SKU ya da sipariş numarasıyla ara"
        className={cn(
          'h-9 w-[11.25rem] rounded-full border border-border bg-bg-sunken pl-9 pr-3 text-[0.8125rem] text-fg placeholder:text-fg-subtle transition-[width] focus:bg-bg-elevated 3xl:w-56',
          'xl:max-3xl:absolute xl:max-3xl:right-0 xl:max-3xl:top-0 xl:max-3xl:z-10 xl:max-3xl:w-9 xl:max-3xl:px-0 xl:max-3xl:text-transparent xl:max-3xl:placeholder:text-transparent',
          'xl:max-3xl:focus:w-64 xl:max-3xl:focus:pl-3 xl:max-3xl:focus:pr-9 xl:max-3xl:focus:text-fg xl:max-3xl:focus:placeholder:text-fg-subtle',
        )}
      />
    </form>
  );
}
